import { formatPercent } from '@/ui/formatters/formatPercent';
import { formatStatusLabel, resolveStatusChipTone } from '@/ui/primitives';
import type { StatusChipStatus } from '@/ui/primitives';
import type { StatusTone } from '@/ui/primitives/Chip';
import type { ReactNode } from 'react';

type MetricCardTone = 'success' | 'warning' | 'error' | 'info' | 'brown' | 'primary' | 'default';

export type MetricCardItem = {
  id: string;
  status: ReactNode;
  statusTone: MetricCardTone;
  count: ReactNode;
  percentage: ReactNode;
  delta?: ReactNode;
  deltaTone?: 'positive' | 'negative' | 'neutral';
};

export type MetricCardOption<TStatus extends string> = {
  value: TStatus;
  label?: string;
  tone?: MetricCardTone;
};

export type BuildMetricCardsArgs<TStatus extends string> = {
  options: Array<MetricCardOption<TStatus>>;
  counts: Partial<Record<TStatus, number>>;
  deltas?: Partial<Record<TStatus, number>>;
  total: number | null | undefined;
  loading: boolean;
  resolveTone?: (status: TStatus) => MetricCardTone;
  showDelta?: boolean;
  resolveDeltaTone?: (delta: number) => MetricCardItem['deltaTone'];
  showTotalCard?: boolean;
  totalLabel?: string;
};

const normalizeMetricTone = (tone: StatusTone): MetricCardTone => {
  switch (tone) {
    case 'neutral':
    case 'default':
    case 'grey':
      return 'default';
    case 'green':
    case 'success':
      return 'success';
    case 'red':
    case 'error':
      return 'error';
    case 'orange':
    case 'yellow':
    case 'warning':
      return 'warning';
    case 'blue':
    case 'info':
      return 'info';
    case 'teal':
      return 'primary';
    case 'transparent':
      return 'default';
    case 'brown':
      return 'brown';
    case 'primary':
    case 'purple':
    case 'pink':
      return 'primary';
  }
};

export const resolveMetricTone = (status: StatusChipStatus | 'none'): MetricCardTone => {
  if (status === 'none') return 'default';
  return normalizeMetricTone(resolveStatusChipTone(status));
};

export const mapStatusOptionsToMetricOptions = <TStatus extends string>(
  options: Array<{ value: TStatus; label: string }>,
  toneResolver: (status: TStatus) => MetricCardTone,
): Array<MetricCardOption<TStatus>> =>
  options.map((option) => ({
    value: option.value,
    label: option.label,
    tone: toneResolver(option.value),
  }));

const formatCount = (value: number, showPlaceholder: boolean): ReactNode => {
  if (showPlaceholder) return '-';
  return value.toLocaleString();
};

const formatPercentage = (count: number, total: number, showPlaceholder: boolean): ReactNode => {
  if (showPlaceholder) return '-';
  if (total <= 0) return '0%';
  return `${formatPercent((count / total) * 100)}%`;
};

const sumMetricValues = (values: Array<number | undefined>): number =>
  values.reduce<number>((sum, value) => sum + (value ?? 0), 0);

export const buildStatusMetricCards = <TStatus extends string>({
  options,
  counts,
  deltas,
  total,
  loading,
  resolveTone,
  showDelta = false,
  resolveDeltaTone,
  showTotalCard = true,
  totalLabel = 'TOTAL',
}: BuildMetricCardsArgs<TStatus>): MetricCardItem[] => {
  const showPlaceholder = loading && (total === null || total === undefined);
  const totalCount = total ?? sumMetricValues(Object.values(counts) as Array<number | undefined>);
  const cards = options.map((option) => {
    const countValue = counts[option.value] ?? 0;
    const label = option.label ?? formatStatusLabel(option.value as StatusChipStatus);
    const tone = option.tone ?? (resolveTone ? resolveTone(option.value) : 'default');
    const rawDelta = deltas?.[option.value];
    const shouldShowDelta = showDelta || rawDelta !== undefined;
    const resolvedDelta =
      rawDelta === undefined || rawDelta === 0
        ? shouldShowDelta
          ? '-'
          : undefined
        : rawDelta > 0
          ? `+${rawDelta}`
          : rawDelta;
    const baseDeltaTone =
      resolvedDelta === undefined || resolvedDelta === '-'
        ? 'neutral'
        : resolveDeltaTone
          ? resolveDeltaTone(rawDelta ?? 0)
          : rawDelta && rawDelta > 0
            ? 'positive'
            : 'negative';
    const shouldInvertDeltaTone = option.value === 'failed' || option.value === 'errored';
    const deltaTone =
      shouldInvertDeltaTone && baseDeltaTone !== 'neutral'
        ? baseDeltaTone === 'positive'
          ? 'negative'
          : 'positive'
        : baseDeltaTone;
    return {
      id: option.value,
      status: label,
      statusTone: tone,
      count: formatCount(countValue, showPlaceholder),
      percentage: formatPercentage(countValue, totalCount, showPlaceholder),
      delta: resolvedDelta,
      deltaTone,
    };
  });
  if (!showTotalCard) {
    return cards;
  }
  const totalDelta = deltas
    ? sumMetricValues(Object.values(deltas) as Array<number | undefined>)
    : undefined;
  const totalDeltaResolved =
    totalDelta === undefined || totalDelta === 0
      ? showDelta
        ? '-'
        : undefined
      : totalDelta > 0
        ? `+${totalDelta}`
        : totalDelta;
  const totalDeltaTone =
    totalDeltaResolved === undefined || totalDeltaResolved === '-'
      ? 'neutral'
      : (totalDelta ?? 0) > 0
        ? 'positive'
        : 'negative';
  return [
    {
      id: 'total',
      status: totalLabel,
      statusTone: 'primary',
      count: formatCount(totalCount, showPlaceholder),
      percentage: undefined,
      delta: totalDeltaResolved,
      deltaTone: totalDeltaTone,
    },
    ...cards,
  ];
};

export const buildStatusDeltaMap = <TStatus extends string>(
  current: Partial<Record<TStatus, number>>,
  previous: Partial<Record<TStatus, number>>,
): Partial<Record<TStatus, number>> => {
  const deltas: Partial<Record<TStatus, number>> = {};
  const keys = new Set<string>([
    ...(Object.keys(current) as TStatus[]),
    ...(Object.keys(previous) as TStatus[]),
  ]);
  keys.forEach((key) => {
    const typedKey = key as TStatus;
    const currentValue = current[typedKey] ?? 0;
    const previousValue = previous[typedKey] ?? 0;
    deltas[typedKey] = currentValue - previousValue;
  });
  return deltas;
};
